import React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Check, RefreshCw, X, Copy, ArrowUp, MoreHorizontal } from 'lucide-react';

interface FindingActionsProps {
  onAction: (action: string) => void;
  disabled?: boolean;
}

export const FindingActions: React.FC<FindingActionsProps> = ({
  onAction,
  disabled = false,
}) => {
  const actions = [
    {
      id: 'resolve',
      label: 'Mark as Resolved',
      icon: Check,
      description: 'Mark this finding as fixed and resolved',
      color: 'text-emerald-400',
    },
    {
      id: 'retest',
      label: 'Request Retest',
      icon: RefreshCw,
      description: 'Request verification of the fix',
      color: 'text-blue-400',
    },
    {
      id: 'false_positive',
      label: 'Mark as False Positive',
      icon: X,
      description: 'Indicate that this is not a real issue',
      color: 'text-purple-400',
    },
    {
      id: 'duplicate',
      label: 'Mark as Duplicate',
      icon: Copy,
      description: 'Mark as duplicate of another finding',
      color: 'text-gray-400',
    },
    {
      id: 'escalate',
      label: 'Escalate',
      icon: ArrowUp,
      description: 'Escalate to security team',
      color: 'text-red-400',
      separator: true,
    },
  ];

  return (
    <DropdownMenu.Root>
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <DropdownMenu.Trigger
              disabled={disabled}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenu.Trigger>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="px-3 py-1.5 text-xs bg-[#1A1F2E] text-gray-300 rounded-lg shadow-xl"
              sideOffset={5}
            >
              Actions
              <Tooltip.Arrow className="fill-[#1A1F2E]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="w-64 bg-[#1A1F2E] rounded-lg p-1.5 shadow-xl border border-gray-800"
          sideOffset={5}
          align="end"
        >
          {actions.map((action, index) => (
            <React.Fragment key={action.id}>
              {action.separator && index !== 0 && (
                <DropdownMenu.Separator className="h-px bg-gray-800 my-1" />
              )}
              <DropdownMenu.Item
                className="outline-none"
                onSelect={() => onAction(action.id)}
              >
                <div className="flex items-start space-x-3 px-3 py-2 rounded-md hover:bg-gray-800/50 cursor-pointer">
                  <div className={`p-2 rounded-lg bg-gray-800/30 ${action.color}`}>
                    <action.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className={`text-sm font-medium ${action.color}`}>
                      {action.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {action.description}
                    </div>
                  </div>
                </div>
              </DropdownMenu.Item>
            </React.Fragment>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};